import { DocumentMapping } from "@/configuration/document-mappings/types";
import { Classification } from "@/src/models/constants/classifications";

export const NVISION_EWEI_TORRANCE_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.ADMIN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: {{ documentType }} received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: null,
    displayName: "Admin",
    documentLabel: "{{ dateReceived }} Admin - {{ patientInitials }}",
    folder: "Other Documents",
    folderMapping: "a8dec21c-f2b1-4a5b-a2ce-83473c444212",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.ADMIN,
    group: "ewei-torrance",
  },
  [Classification.IMAGING_RESULTS_RETINAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Retinal Imaging",
      "Assigned To: Physician",
      "Task Details: {{ studyType }} imaging on {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Physician",
    displayName: "Retinal Imaging",
    documentLabel: "{{ dateOfEvent }} Retinal - {{ patientInitials }}",
    folder: "H&Ps / Labs",
    folderMapping: "3deaecca-411a-4326-96e1-8f5dd3686594",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_RETINAL,
    group: "ewei-torrance",
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Junk",
    documentLabel: "{{ dateReceived }} Junk - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    priority: "Normal",
    classification: Classification.JUNK,
    group: "ewei-torrance",
  },
  [Classification.LAB_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Lab Results",
      "Assigned To: Physician",
      "Task Details: {{ nameOfLab }} lab result collected {{ collectionDate }}. Please review in Document Management.",
    ],
    assignedTo: "Physician",
    displayName: "Lab Results",
    documentLabel: "{{ collectionDate }} Lab - {{ patientInitials }}",
    folder: "H&Ps / Labs",
    folderMapping: "3deaecca-411a-4326-96e1-8f5dd3686594",
    inputDate: "Date of Collection",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS,
    group: "ewei-torrance",
  },
  [Classification.LAB_RESULTS_PATHOLOGY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Lab Results Pathology",
      "Assigned To: Physician",
      "Task Details: {{ listOfNameOfLabs }} result collected {{ collectionDate }}. If Marrone + surgical, a copy is also emailed to Diane automatically. Please review in Document Management.",
    ],
    assignedTo: "Physician",
    displayName: "Lab Results Pathology",
    documentLabel: "{{ collectionDate }} Pathology - {{ patientInitials }}",
    folder: "H&Ps / Labs",
    folderMapping: "3deaecca-411a-4326-96e1-8f5dd3686594",
    inputDate: "Date of Collection",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS_PATHOLOGY,
    group: "ewei-torrance",
  },
  [Classification.MEDICAL_HISTORY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Medical History",
      "Assigned To: Physician",
      "Task Details: Outside records from {{ companyName }} received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: "Physician",
    displayName: "Medical History",
    documentLabel: "{{ dateReceived }} Medical History - {{ patientInitials }}",
    folder: "Previous Medical Records",
    folderMapping: "db597296-db67-44e7-8bf6-b7f4045e22c2",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.MEDICAL_HISTORY,
    group: "ewei-torrance",
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Records request from {{ requestingLocation }} received {{ dateReceived }}.",
      "Chart Note Subject: Medical Record Request",
      "Chart Note Entry: Received Medical Record Request on {{ dateReceived }}.",
    ],
    assignedTo: "rachel.white@nvisioncenters.com",
    displayName: "Medical Record Request",
    documentLabel: "{{ dateReceived }} Records Request - {{ patientInitials }}",
    folder: "Medical Records Request",
    folderMapping: "de5499d8-b131-4175-ad1d-b6b7ef553397",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: "ewei-torrance",
  },
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Bulk records request received {{ dateReceived }}. Do not file to a single chart. Email to Rachel White for outside records service handoff.",
    ],
    assignedTo: "rachel.white@nvisioncenters.com",
    displayName: "HEDIS Records Request",
    documentLabel: "{{ dateReceived }} HEDIS Request - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    priority: "Normal",
    classification: Classification.MEDICAL_RECORD_REQUEST_HEDIS,
    group: "ewei-torrance",
  },
  [Classification.NOTIFICATION_APPOINTMENT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Appointment Notification",
    documentLabel: "{{ dateReceived }} Appt Notification - {{ patientInitials }}",
    folder: "Referral Network Ppw - General",
    folderMapping: "499341e1-5ddc-41b4-8365-6d1b009a4c29",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.NOTIFICATION_APPOINTMENT,
    group: "ewei-torrance",
  },
  [Classification.OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Document received {{ dateReceived }} has no defined protocol or low confidence. Reason: {{ forwardingReason }}. Emailed to Rachel White for review, no patient chart filed.",
    ],
    assignedTo: "rachel.white@nvisioncenters.com",
    displayName: "Other",
    documentLabel: "{{ dateReceived }} Other",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    priority: "Normal",
    classification: Classification.OTHER,
    group: "ewei-torrance",
  },
  [Classification.PHARMACY_RELATED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Pharmacy Related",
    documentLabel: "{{ dateReceived }} Pharmacy - {{ patientInitials }}",
    folder: "Medications",
    folderMapping: "efc58f6b-7922-49ff-ba61-a911fd192d1a",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED,
    group: "ewei-torrance",
  },
  [Classification.PHARMACY_RELATED_CLARIFICATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Pharmacy Clarification",
      "Assigned To: Physician",
      "Task Details: Clarification requested for {{ medicationName }} on {{ dateReceived }}. Please review in Document Management.",
      "Chart Note Subject: Pharmacy Clarification",
      "Chart Note Entry: Received Pharmacy Clarification on {{ dateReceived }}.",
    ],
    assignedTo: "Physician",
    displayName: "Pharmacy Clarification",
    documentLabel: "{{ dateReceived }} Rx Clarification - {{ patientInitials }}",
    folder: "Medications",
    folderMapping: "efc58f6b-7922-49ff-ba61-a911fd192d1a",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED_CLARIFICATION,
    group: "ewei-torrance",
  },
  [Classification.PHARMACY_RELATED_REFILL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Pharmacy Refill",
      "Assigned To: Physician",
      "Task Details: Refill request for {{ medicationName }} received {{ dateReceived }}. Please review in Document Management.",
      "Chart Note Subject: Pharmacy Refill",
      "Chart Note Entry: Received Pharmacy Refill on {{ dateReceived }}.",
    ],
    assignedTo: "Physician",
    displayName: "Pharmacy Refill",
    documentLabel: "{{ dateReceived }} Rx Refill - {{ patientInitials }}",
    folder: "Medications",
    folderMapping: "efc58f6b-7922-49ff-ba61-a911fd192d1a",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED_REFILL,
    group: "ewei-torrance",
  },
  [Classification.PRIOR_AUTHORIZATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: PA {{ authorizationStatus }} received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: "janelle.campos@nvisioncenters.com",
    displayName: "Prior Authorization",
    documentLabel: "{{ dateReceived }} Prior Auth - {{ patientInitials }}",
    folder: "Authorization",
    folderMapping: "eb796840-574a-48d1-bd19-b0ab82317550",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION,
    group: "ewei-torrance",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Prior Authorization Medication Request",
      "Assigned To: valerie.lopez@nvisioncenters.com",
      "Task Details: PA request for {{ medicationName }} received {{ dateReceived }}. Please review in Document Management.",
      "Chart Note Subject: Prior Authorization Medication Request",
      "Chart Note Entry: Received Prior Authorization Medication Request on {{ dateReceived }}.",
    ],
    assignedTo: "valerie.lopez@nvisioncenters.com",
    displayName: "Prior Authorization Medication Request",
    documentLabel: "{{ dateReceived }} Med PA Request - {{ patientInitials }}",
    folder: "Medications",
    folderMapping: "efc58f6b-7922-49ff-ba61-a911fd192d1a",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    group: "ewei-torrance",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Prior Authorization Medication Response",
      "Assigned To: valerie.lopez@nvisioncenters.com",
      "Task Details: PA {{ authorizationStatus }} for {{ medicationName }} received {{ dateReceived }}. Please review in Document Management.",
      "Chart Note Subject: Prior Authorization Medication Response",
      "Chart Note Entry: Received Prior Authorization Medication Response on {{ dateReceived }}.",
    ],
    assignedTo: "valerie.lopez@nvisioncenters.com",
    displayName: "Prior Authorization Medication Response",
    documentLabel: "{{ dateReceived }} Med PA Response - {{ patientInitials }}",
    folder: "Medications",
    folderMapping: "efc58f6b-7922-49ff-ba61-a911fd192d1a",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    group: "ewei-torrance",
  },
  [Classification.PROGRESS_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Create EHR Task",
      "Subject: Progress Note",
      "Assigned To: Physician",
      "Task Details: Progress note from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Physician",
    displayName: "Progress Note",
    documentLabel: "{{ dateOfEvent }} Progress Note - {{ patientInitials }}",
    folder: "Previous Medical Records",
    folderMapping: "db597296-db67-44e7-8bf6-b7f4045e22c2",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROGRESS_NOTE,
    group: "ewei-torrance",
  },
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Referral from {{ referringProvider }} received {{ dateReceived }}. Please schedule.",
      "Chart Note Subject: Referral",
      "Chart Note Entry: Received Referral on {{ dateReceived }}.",
    ],
    assignedTo: "emily.fuentes@nvisioncenters.com",
    displayName: "Referral",
    documentLabel: "{{ dateReceived }} Referral - {{ patientInitials }}",
    folder: "Referral Network Ppw - General",
    folderMapping: "499341e1-5ddc-41b4-8365-6d1b009a4c29",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    priority: "Normal",
    classification: Classification.REFERRAL,
    group: "ewei-torrance",
  },
  [Classification.REFERRAL_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Referral response ({{ referralResponse }}) received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: "emily.fuentes@nvisioncenters.com",
    displayName: "Referral Response",
    documentLabel: "{{ dateReceived }} Referral Response - {{ patientInitials }}",
    folder: "Referral Network Ppw - General",
    folderMapping: "499341e1-5ddc-41b4-8365-6d1b009a4c29",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.REFERRAL_RESPONSE,
    group: "ewei-torrance",
  },
} as const;
